import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon } from './Icons';

const CustomSelectDropdown = ({ options, value, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                handleClose();
            }
        };

        if (isOpen || isAnimating) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen, isAnimating]);

    const handleToggle = () => {
        if (isOpen) {
            handleClose();
        } else {
            handleOpen();
        }
    };

    const handleOpen = () => {
        setIsOpen(true);
        setIsAnimating(true);
        setSearchTerm(''); // Clear search when opening
    };

    const handleClose = () => {
        setIsAnimating(false);
        setSearchTerm(''); // Clear search when closing
        // Wait for animation to complete before hiding
        setTimeout(() => {
            setIsOpen(false);
        }, 200);
    };

    const handleSelect = (optionValue) => {
        onChange(optionValue);
        handleClose();
    };

    const selectedOption = options.find(opt => opt.value === value);

    // Filter options based on search term
    const filteredOptions = searchTerm
        ? options.filter(option =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : options;

    return (
        <div className="custom-select-dropdown" ref={dropdownRef}>
            <div
                className="custom-select-header"
                onClick={handleToggle}
            >
                <span className="custom-select-text">
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDownIcon className={`chevron ${isOpen ? 'rotated' : ''}`} />
            </div>

            {(isOpen || isAnimating) && (
                <div className={`custom-select-dropdown-content ${isOpen ? 'open' : 'closed'}`}>
                    <div className="search-container" style={{ padding: '8px 18px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                outline: 'none'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(option => (
                            <div
                                key={option.value}
                                className={`custom-select-option ${value === option.value ? 'selected' : ''}`}
                                onClick={() => handleSelect(option.value)}
                            >
                                <span className="option-text">{option.label}</span>
                                {value === option.value && <span className="checkmark">✓</span>}
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: '12px 18px', color: '#6b7280', fontSize: '14px' }}>
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen]);

    const handleToggle = (value) => {
        const newValues = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value];
        onChange(newValues);
    };

    const handleSelectAll = () => {
        if (selectedValues.length === filteredOptions.length) {
            onChange([]);
        } else {
            onChange(filteredOptions.map(opt => opt.value));
        }
    };

    // Filter options based on search term
    const filteredOptions = searchTerm
        ? options.filter(option =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : options;

    return (
        <div className="multi-select-dropdown" ref={dropdownRef}>
            <div
                className="multi-select-header"
                onClick={() => {
                    setIsOpen(!isOpen);
                    if (!isOpen) setSearchTerm(''); // Clear search when opening
                }}
            >
                <span className="multi-select-text">
                    {selectedValues.length === 0
                        ? placeholder
                        : `${selectedValues.length} filter${selectedValues.length !== 1 ? 's' : ''} selected`
                    }
                </span>
                <ChevronDownIcon className={`chevron ${isOpen ? 'rotated' : ''}`} />
            </div>

            {isOpen && (
                <div className="multi-select-dropdown-content">
                    <div className="search-container" style={{ padding: '8px 18px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                outline: 'none'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="multi-select-option select-all" onClick={handleSelectAll}>
                        <input
                            type="checkbox"
                            checked={selectedValues.length === filteredOptions.length && filteredOptions.length > 0}
                            onChange={() => {}}
                        />
                        <span>{selectedValues.length === filteredOptions.length ? 'Deselect All' : 'Select All'} ({filteredOptions.length})</span>
                    </div>

                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(option => (
                            <div
                                key={option.value}
                                className="multi-select-option"
                                onClick={() => handleToggle(option.value)}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedValues.includes(option.value)}
                                    onChange={() => {}}
                                />
                                <span className="option-text">{option.label}</span>
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: '12px 18px', color: '#6b7280', fontSize: '14px' }}>
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const StarRating = ({ rating }) => {
    if (!rating || rating === 0) return null;
    
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
    
    return (
        <div className="star-rating">
            {[...Array(fullStars)].map((_, i) => 
                <span key={`full-${i}`} className="star star-full">★</span>
            )}
            {halfStar && <span className="star star-half">★</span>}
            {[...Array(emptyStars)].map((_, i) => 
                <span key={`empty-${i}`} className="star star-empty">★</span>
            )}
            <span className="rating-text">({rating})</span>
        </div>
    );
};

const BettrScore = ({ score }) => {
    if (!score || score === 0) return null;
    
    return (
        <div className="bettr-score">
            <span className="score-value">{score}</span>
            <span className="score-unit">%</span>
        </div>
    );
};

export { CustomSelectDropdown, MultiSelectDropdown, StarRating, BettrScore };
